import React, { useEffect, useState } from "react";
import { ClipboardList, Award, BookOpen, GraduationCap } from "lucide-react";
import { RadialProgress, ProgressBar, EmptyState } from "../ui";
import apiClient from "../../utils/apiClient";

/**
 * StudentAssignmentCard — Assignment analytics card with Exam | Practice toggle.
 * Shows radial completion-rate, graded/submitted/pending breakdown, and avg score
 * for the selected assignment type.
 *
 * Props:
 *   studentId — MongoDB student _id
 */
const StudentAssignmentCard = ({ studentId }) => {
  const [stats, setStats] = useState({ passed: 0, failed: 0, totalAssignments: 0, byType: {} });
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    apiClient
      .get("/assignment-sessions/stats", { params: { studentId } })
      .then((res) => setStats(res.data || { passed: 0, failed: 0, totalAssignments: 0, byType: {} }))
      .catch(() => setStats({ passed: 0, failed: 0, totalAssignments: 0, byType: {} }))
      .finally(() => setLoading(false));
  }, [studentId]);

  // Derive current-view stats based on active toggle
  const byType = stats.byType || {};
  const hasExam = (byType.exam?.sessions || 0) > 0;
  const hasPractice = (byType.practice?.sessions || 0) > 0;
  const showToggle = hasExam || hasPractice;

  let viewStats;
  if (activeType === "exam" && byType.exam) {
    viewStats = { total: byType.exam.sessions, passed: byType.exam.passed, failed: byType.exam.failed };
  } else if (activeType === "practice" && byType.practice) {
    viewStats = { total: byType.practice.sessions, passed: byType.practice.passed, failed: byType.practice.failed };
  } else {
    viewStats = { total: stats.totalAssignments || 0, passed: stats.passed || 0, failed: stats.failed || 0 };
  }

  const submitted = viewStats.passed + viewStats.failed;
  const pending = Math.max(viewStats.total - submitted, 0);
  const completionRate = viewStats.total > 0 ? Math.round((submitted / viewStats.total) * 100) : 0;
  const avgScore = submitted > 0 ? Math.round((viewStats.passed / submitted) * 100) : 0;

  const TYPE_TABS = [
    { key: "all", label: "All", icon: <ClipboardList size={12} /> },
    { key: "exam", label: "Exam", icon: <GraduationCap size={12} /> },
    { key: "practice", label: "Practice", icon: <BookOpen size={12} /> },
  ];

  if (loading) {
    return (
      <div className="ui-card sp-analytics-card">
        <div className="sp-analytics-gradient sp-analytics-gradient--amber">
          <div className="sp-analytics-header">
            <div className="sp-analytics-header-icon sp-analytics-header-icon--amber">
              <ClipboardList size={18} color="#fff" />
            </div>
            <div>
              <h3 className="sp-analytics-header-title">Assignments</h3>
              <p className="sp-analytics-header-sub">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((stats.totalAssignments || 0) === 0) {
    return (
      <div className="ui-card sp-analytics-card">
        <EmptyState
          icon={<ClipboardList size={28} />}
          title="No Assignments Yet"
          description="Assignments will appear here"
        />
      </div>
    );
  }

  return (
    <div className="ui-card sp-analytics-card">
      <div className="sp-analytics-gradient sp-analytics-gradient--amber">
        <div className="sp-analytics-header">
          <div className="sp-analytics-header-icon sp-analytics-header-icon--amber">
              <ClipboardList size={18} color="#fff" />
          </div>
          <div>
            <h3 className="sp-analytics-header-title">Assignments</h3>
            <p className="sp-analytics-header-sub">{stats.totalAssignments} total assignments</p>
          </div>
        </div>

        {/* Type toggle */}
        {showToggle && (
          <div className="sp-analytics-toggle">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`sp-analytics-toggle-btn${activeType === tab.key ? " sp-analytics-toggle-btn--active" : ""}`}
                onClick={() => setActiveType(tab.key)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="sp-analytics-body">
          <RadialProgress value={completionRate} color="#f59e0b" label="Submitted" />
          <div className="sp-analytics-stats">
            <div className="sp-analytics-stat-row">
              <div className="sp-analytics-stat-label">
                <span className="sp-analytics-dot sp-analytics-dot--emerald" />
                Passed
              </div>
              <span className="sp-analytics-stat-value sp-analytics-stat-value--emerald">{viewStats.passed}</span>
            </div>
            <div className="sp-analytics-stat-row">
              <div className="sp-analytics-stat-label">
                <span className="sp-analytics-dot sp-analytics-dot--blue" />
                Failed
              </div>
              <span className="sp-analytics-stat-value sp-analytics-stat-value--blue">{viewStats.failed}</span>
            </div>
            <div className="sp-analytics-stat-row">
              <div className="sp-analytics-stat-label">
                <span className="sp-analytics-dot sp-analytics-dot--amber" />
                Pending
              </div>
              <span className="sp-analytics-stat-value sp-analytics-stat-value--amber">{pending}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="sp-analytics-chart">
        <div className="sp-analytics-score-header">
          <p className="sp-analytics-chart-label">AVG SCORE</p>
          <div className="sp-analytics-score-value">
            <Award size={14} className="sp-analytics-score-icon" />
            <span>{avgScore}%</span>
          </div>
        </div>
        <ProgressBar value={avgScore} size="md" color="amber" />
      </div>

      {/* Type breakdown summary at bottom */}
      {(hasExam || hasPractice) && activeType === "all" && (
        <div className="sp-analytics-type-summary">
          {hasExam && (
            <div className="sp-analytics-type-row">
              <GraduationCap size={13} />
              <span className="sp-analytics-type-label">Exams</span>
              <span className="sp-analytics-type-val">{byType.exam.sessions}</span>
              <span className="sp-analytics-type-detail sp-analytics-type-detail--emerald">{byType.exam.passed} passed</span>
            </div>
          )}
          {hasPractice && (
            <div className="sp-analytics-type-row">
              <BookOpen size={13} />
              <span className="sp-analytics-type-label">Practice</span>
              <span className="sp-analytics-type-val">{byType.practice.sessions}</span>
              <span className="sp-analytics-type-detail sp-analytics-type-detail--emerald">{byType.practice.passed} passed</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentAssignmentCard;
